import { useTranslation } from 'react-i18next'
import type { TemplateVariable } from '../../services/templatesApi'

interface VariableFormProps {
  variables: TemplateVariable[]
  values: Record<string, string | number | boolean>
  onChange: (name: string, value: string | number | boolean) => void
  errors: Record<string, string>
}

/**
 * Dynamic form rendered from TemplateVariable[] definitions.
 * Groups variables by the `group` field, renders appropriate input types.
 */
export function VariableForm({ variables, values, onChange, errors }: VariableFormProps) {
  const { t } = useTranslation()

  // Group variables by their group field
  const sorted = [...variables].sort((a, b) => a.order - b.order)
  const groups = new Map<string, TemplateVariable[]>()
  for (const v of sorted) {
    const group = v.group || ''
    if (!groups.has(group)) groups.set(group, [])
    groups.get(group)!.push(v)
  }

  return (
    <div className="space-y-4">
      {[...groups.entries()].map(([group, vars]) => (
        <div key={group}>
          {group && (
            <h4 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">
              {group}
            </h4>
          )}
          <div className="space-y-2.5">
            {vars.map((variable) => (
              <VariableField
                key={variable.id}
                variable={variable}
                value={values[variable.name] ?? variable.default_value ?? ''}
                onChange={(val) => onChange(variable.name, val)}
                error={errors[variable.name]}
              />
            ))}
          </div>
        </div>
      ))}
      {variables.length === 0 && (
        <p className="text-xs text-neutral-400 text-center py-2">
          {t('templates.noVariables')}
        </p>
      )}
    </div>
  )
}

// ============================================================
// Individual field renderer
// ============================================================

interface VariableFieldProps {
  variable: TemplateVariable
  value: string | number | boolean
  onChange: (value: string | number | boolean) => void
  error?: string
}

function VariableField({ variable, value, onChange, error }: VariableFieldProps) {
  const inputClasses = `w-full px-2 py-1.5 text-xs rounded-md border
    ${error
      ? 'border-error-400 focus:ring-error-500/30'
      : 'border-neutral-200 dark:border-neutral-700 focus:ring-primary-500/30 focus:border-primary-500'
    }
    bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100
    placeholder:text-neutral-500 focus:outline-none focus:ring-1`

  return (
    <div>
      <label className="flex items-center gap-1 text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
        {variable.label}
        {variable.is_required && <span className="text-error-500">*</span>}
      </label>

      {variable.description && (
        <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mb-1">
          {variable.description}
        </p>
      )}

      {/* Checkbox */}
      {variable.field_type === 'checkbox' && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500/30"
          />
          <span className="text-xs text-neutral-700 dark:text-neutral-300">
            {variable.label}
          </span>
        </label>
      )}

      {/* Select */}
      {variable.field_type === 'select' && (
        <select
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          className={inputClasses}
        >
          <option value="">—</option>
          {variable.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      {/* Textarea */}
      {variable.field_type === 'textarea' && (
        <textarea
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className={inputClasses + ' resize-y'}
          required={variable.is_required}
        />
      )}

      {/* All other text-like inputs */}
      {!['checkbox', 'select', 'textarea'].includes(variable.field_type) && (
        <input
          type={getInputType(variable.field_type)}
          value={String(value)}
          onChange={(e) => {
            if (variable.field_type === 'number' || variable.field_type === 'currency') {
              onChange(e.target.value === '' ? '' : Number(e.target.value))
            } else {
              onChange(e.target.value)
            }
          }}
          className={inputClasses}
          required={variable.is_required}
          min={variable.min_value ?? undefined}
          max={variable.max_value ?? undefined}
          minLength={variable.min_length ?? undefined}
          maxLength={variable.max_length ?? undefined}
          pattern={variable.regex_pattern || undefined}
          step={variable.field_type === 'currency' ? '0.01' : undefined}
        />
      )}

      {error && (
        <p className="text-[10px] text-error-500 mt-0.5">{error}</p>
      )}
    </div>
  )
}

function getInputType(fieldType: string): string {
  switch (fieldType) {
    case 'number':
    case 'currency':
      return 'number'
    case 'date':
      return 'date'
    case 'email':
      return 'email'
    case 'phone':
      return 'tel'
    default:
      return 'text'
  }
}
